/**
 * XML Parser utilities for CFDI invoice processing
 * Extracts key data from XML without requiring AI
 */

import type { CfdiType } from '../types/invoice';
import { logger } from '@/lib/logger';

/**
 * Extract TipoDeComprobante from CFDI XML
 * Returns 'I' (Ingreso), 'E' (Egreso), 'P' (Pago), 'N' (Nómina), 'T' (Traslado)
 */
export const extractCfdiType = async (xmlFile: File): Promise<CfdiType | null> => {
  try {
    const content = await xmlFile.text();
    const match = content.match(/TipoDeComprobante\s*=\s*["']([IEPNT])["']/i);
    return match ? (match[1].toUpperCase() as CfdiType) : null;
  } catch {
    return null;
  }
};

/**
 * Extract related UUIDs from CfdiRelacionados section
 * Works for both CfdiRelacionado elements and pago20:DoctoRelacionado
 */
export const extractRelatedUuids = async (xmlFile: File): Promise<string[]> => {
  try {
    const content = await xmlFile.text();
    const uuids: string[] = [];

    // Pattern 1: CfdiRelacionado UUID (for credit notes, general relations)
    const cfdiRelPattern = /CfdiRelacionado[^>]*UUID\s*=\s*["']([A-Fa-f0-9-]{36})["']/gi;
    let match;
    while ((match = cfdiRelPattern.exec(content)) !== null) {
      uuids.push(match[1].toUpperCase());
    }

    // Pattern 2: DoctoRelacionado IdDocumento (for payment complements)
    const doctoPattern = /DoctoRelacionado[^>]*IdDocumento\s*=\s*["']([A-Fa-f0-9-]{36})["']/gi;
    while ((match = doctoPattern.exec(content)) !== null) {
      const uuid = match[1].toUpperCase();
      if (!uuids.includes(uuid)) uuids.push(uuid);
    }

    return uuids;
  } catch {
    return [];
  }
};

/**
 * Extract payment complement data from CFDI tipo P XML
 * Parses the pago20:Pago node and DoctoRelacionado elements
 */
export const extractPaymentComplementData = async (
  xmlFile: File
): Promise<{
  paymentDate?: string;
  paymentForm?: string;
  amount?: number;
  currency: string;
  partialityNumber?: number;
  previousBalance?: number;
  amountPaid?: number;
  remainingBalance?: number;
} | null> => {
  try {
    const content = await xmlFile.text();

    // pago20:Pago or Pago node attributes
    const pagoSection = content.match(/(?:pago20:|pago10:)?Pago\s[^>]*>/i);
    if (!pagoSection) return null;
    const pagoStr = pagoSection[0];

    const dateMatch = pagoStr.match(/FechaPago\s*=\s*["']([^"']+)["']/i);
    const formMatch = pagoStr.match(/FormaDePagoP\s*=\s*["']([^"']+)["']/i);
    const amountMatch = pagoStr.match(/Monto\s*=\s*["']([0-9.]+)["']/i);
    const currencyMatch = pagoStr.match(/MonedaP\s*=\s*["']([^"']+)["']/i);

    // DoctoRelacionado attributes
    const docto = content.match(/DoctoRelacionado[^>]*>/i);
    let partialityNumber: number | undefined;
    let previousBalance: number | undefined;
    let amountPaid: number | undefined;
    let remainingBalance: number | undefined;

    if (docto) {
      const d = docto[0];
      const partMatch = d.match(/NumParcialidad\s*=\s*["'](\d+)["']/i);
      const prevMatch = d.match(/ImpSaldoAnt\s*=\s*["']([0-9.]+)["']/i);
      const paidMatch = d.match(/ImpPagado\s*=\s*["']([0-9.]+)["']/i);
      const remMatch = d.match(/ImpSaldoInsoluto\s*=\s*["']([0-9.]+)["']/i);
      if (partMatch) partialityNumber = parseInt(partMatch[1]);
      if (prevMatch) previousBalance = parseFloat(prevMatch[1]);
      if (paidMatch) amountPaid = parseFloat(paidMatch[1]);
      if (remMatch) remainingBalance = parseFloat(remMatch[1]);
    }

    return {
      paymentDate: dateMatch ? dateMatch[1].split('T')[0] : undefined,
      paymentForm: formMatch ? formMatch[1] : undefined,
      amount: amountMatch ? parseFloat(amountMatch[1]) : undefined,
      currency: currencyMatch ? currencyMatch[1] : 'MXN',
      partialityNumber,
      previousBalance,
      amountPaid,
      remainingBalance,
    };
  } catch {
    return null;
  }
};

/**
 * Extract UUID from CFDI XML content
 * The UUID is in the TimbreFiscalDigital complement
 */
export const extractUuidFromXml = async (xmlFile: File): Promise<string | null> => {
  try {
    const content = await xmlFile.text();

    // Try multiple patterns for UUID extraction
    // Pattern 1: tfd:TimbreFiscalDigital UUID attribute
    const tfdPattern = /tfd:TimbreFiscalDigital[^>]*UUID\s*=\s*["']([A-Fa-f0-9-]{36})["']/i;
    let match = content.match(tfdPattern);

    if (match) {
      return match[1].toUpperCase();
    }

    // Pattern 2: TimbreFiscalDigital without namespace prefix
    const noNsPattern = /TimbreFiscalDigital[^>]*UUID\s*=\s*["']([A-Fa-f0-9-]{36})["']/i;
    match = content.match(noNsPattern);

    if (match) {
      return match[1].toUpperCase();
    }

    // Pattern 3: Just look for UUID attribute with valid format
    const uuidPattern = /UUID\s*=\s*["']([A-Fa-f0-9-]{36})["']/i;
    match = content.match(uuidPattern);

    if (match) {
      return match[1].toUpperCase();
    }

    return null;
  } catch (error) {
    logger.error('Error parsing XML', { error });
    return null;
  }
};

/**
 * Extract invoice date from CFDI XML content
 * Returns the Fecha attribute from the Comprobante root element
 */
export const extractDateFromXml = async (xmlFile: File): Promise<string | null> => {
  try {
    const content = await xmlFile.text();

    // Pattern: Fecha attribute in Comprobante element
    // Format: Fecha="2026-01-15T10:30:00"
    const datePattern = /Comprobante[^>]*Fecha\s*=\s*["']([^"']+)["']/i;
    const match = content.match(datePattern);

    if (match) {
      // Return just the date part (YYYY-MM-DD)
      return match[1].split('T')[0];
    }

    // Alternative: FechaTimbrado in TimbreFiscalDigital
    const timbradoPattern = /FechaTimbrado\s*=\s*["']([^"']+)["']/i;
    const timbradoMatch = content.match(timbradoPattern);

    if (timbradoMatch) {
      return timbradoMatch[1].split('T')[0];
    }

    return null;
  } catch (error) {
    logger.error('Error parsing XML date', { error });
    return null;
  }
};

/**
 * Extract project name from CFDI XML content
 * Searches in Concepto descriptions for known project keywords
 */
export const extractProjectFromXml = async (
  xmlFile: File,
  knownProjects: string[]
): Promise<string | null> => {
  try {
    const content = await xmlFile.text();
    const contentUpper = content.toUpperCase();

    // Look for known project names in the XML content
    for (const project of knownProjects) {
      const projectUpper = project.toUpperCase();
      // Check in Descripcion attributes
      if (contentUpper.includes(projectUpper)) {
        return project;
      }
      // Also check for variations (with underscores, spaces, etc.)
      const variations = [
        projectUpper.replace(/_/g, ' '),
        projectUpper.replace(/ /g, '_'),
        projectUpper.replace(/ /g, ''),
      ];
      for (const variation of variations) {
        if (contentUpper.includes(variation)) {
          return project;
        }
      }
    }

    return null;
  } catch (error) {
    logger.error('Error extracting project from XML', { error });
    return null;
  }
};

/**
 * Get the base filename without extension
 */
export const getFileBaseName = (filename: string): string => {
  const lastDot = filename.lastIndexOf('.');
  return lastDot > 0 ? filename.substring(0, lastDot) : filename;
};

/**
 * Validate that two files have matching base names
 */
export const validateMatchingFilenames = (
  xmlFile: File | null,
  pdfFile: File | null
): { valid: boolean; error?: string } => {
  if (!xmlFile || !pdfFile) {
    return { valid: true }; // Can't validate if files not loaded yet
  }

  const xmlBaseName = getFileBaseName(xmlFile.name);
  const pdfBaseName = getFileBaseName(pdfFile.name);

  if (xmlBaseName.toLowerCase() !== pdfBaseName.toLowerCase()) {
    return {
      valid: false,
      error: `Los archivos deben tener el mismo nombre. XML: "${xmlBaseName}" ≠ PDF: "${pdfBaseName}"`,
    };
  }

  return { valid: true };
};
